import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Button from "../../componentes/button/button";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import Input from "../../componentes/input/input";
import { Loading } from "../../componentes/loading/loading";
import { useAuth } from "../../context/authContext";
import DespesaRecorrenteService from "../../services/resource/despesaRecorrenteService";
import UsuarioService from "../../services/resource/usuarioService";
import FilialService from "../../services/resource/filialService";
import CalendarioDespesasRecorrentes from "../../components/solicitacoes/DespesasRecorrentes/CalendarioDespesasRecorrentes";
import ListaPendenciasDespesasRecorrentes from "../../components/solicitacoes/DespesasRecorrentes/ListaPendenciasDespesasRecorrentes";
import ModalCadastroDespesaRecorrente from "../../components/solicitacoes/DespesasRecorrentes/ModalCadastroDespesaRecorrente";
import DetalheOcorrenciaDespesaRecorrente from "../../components/solicitacoes/DespesasRecorrentes/DetalheOcorrenciaDespesaRecorrente";

const service = new DespesaRecorrenteService();
const usuarioService = new UsuarioService();
const filialService = new FilialService();

const STATUS_OPTIONS = [
    { value: "PENDENTE", label: "Pendente" },
    { value: "ENVIADO", label: "Enviado" },
    { value: "MARCADO_MANUALMENTE", label: "Marcado manualmente" },
    { value: "ATRASADO", label: "Atrasado" },
    { value: "PAGO", label: "Pago" },
    { value: "CANCELADA", label: "Cancelada" },
];

const ATIVO_OPTIONS = [
    { value: true, label: "Ativas" },
    { value: false, label: "Inativas" },
];

export default function DespesasRecorrentes() {
    const { user } = useAuth();
    const isAdmin = !!user?.perfis?.includes("ADMIN");

    const [mes, setMes] = useState(dayjs().format("YYYY-MM"));
    const [visualizacao, setVisualizacao] = useState("calendario");
    const [filtros, setFiltros] = useState({ status: null, filialId: null, ativo: null, usuarioId: null });
    const [fornecedorBusca, setFornecedorBusca] = useState("");

    const [ocorrencias, setOcorrencias] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(false);

    const [usuarios, setUsuarios] = useState([]);
    const [filiais, setFiliais] = useState([]);

    const [modalCadastroAberto, setModalCadastroAberto] = useState(false);
    const [despesaRecorrenteEditando, setDespesaRecorrenteEditando] = useState(null);
    const [ocorrenciaSelecionadaId, setOcorrenciaSelecionadaId] = useState(null);

    useEffect(() => {
        document.title = "MyDash | Despesas Recorrentes";
    }, []);

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
        if (isAdmin) {
            usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const carregar = useCallback(() => {
        const parametros = {
            competencia: mes,
            status: filtros.status,
            filialId: filtros.filialId,
            ativo: filtros.ativo,
            usuarioId: isAdmin ? filtros.usuarioId : null,
        };
        setLoading(true);
        Promise.all([service.listarOcorrencias(parametros), service.kpis(parametros)])
            .then(([resOcorrencias, resKpis]) => {
                setOcorrencias(resOcorrencias.data);
                setKpis(resKpis.data);
            })
            .catch(() => {
                setOcorrencias([]);
                setKpis(null);
            })
            .finally(() => setLoading(false));
    }, [mes, filtros, isAdmin]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const ocorrenciasFiltradas = useMemo(() => {
        if (!fornecedorBusca.trim()) return ocorrencias;
        const termo = fornecedorBusca.trim().toLowerCase();
        return ocorrencias.filter(o => (o.fornecedorNome || "").toLowerCase().includes(termo) || (o.fornecedorFantasia || "").toLowerCase().includes(termo));
    }, [ocorrencias, fornecedorBusca]);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    function abrirNovaDespesaRecorrente() {
        setDespesaRecorrenteEditando(null);
        setModalCadastroAberto(true);
    }

    function abrirEdicaoRecorrencia(despesaRecorrenteId) {
        service.buscar(despesaRecorrenteId).then(res => {
            setDespesaRecorrenteEditando(res.data);
            setOcorrenciaSelecionadaId(null);
            setModalCadastroAberto(true);
        });
    }

    function aoSalvarCadastro() {
        setModalCadastroAberto(false);
        setDespesaRecorrenteEditando(null);
        carregar();
    }

    return (
        <main className="px-4 py-6">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-2xl font-bold text-foreground">Despesas Recorrentes</h1>
                        <p className="text-sm text-muted-foreground mt-1">Controle de agenda e lançamento das suas despesas periódicas.</p>
                    </div>
                    <Button variant="blue" label="Nova Despesa Recorrente" iconLeft="fa fa-plus" onClick={abrirNovaDespesaRecorrente} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <KpiCard label="Pendentes" value={kpis?.pendentes ?? "-"} icon="fa fa-hourglass-half" className="bg-amber-100 text-amber-700" />
                    <KpiCard label="Enviadas" value={kpis?.enviadas ?? "-"} icon="fa fa-paper-plane" className="bg-emerald-100 text-emerald-700" />
                    <KpiCard label="Pagas" value={kpis?.pagas ?? "-"} icon="fa fa-check-circle" className="bg-teal-100 text-teal-700" />
                    <KpiCard label="Atrasadas" value={kpis?.atrasadas ?? "-"} icon="fa fa-triangle-exclamation" className="bg-red-100 text-red-700" />
                    <KpiCard label="Total" value={kpis?.total ?? "-"} icon="fa fa-list" className="bg-primary/10 text-primary" />
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card p-4">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Status</label>
                            <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS} placeholder="Todos" showClear />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Filial</label>
                            <CustomDropdown value={filtros.filialId} onChange={e => setFiltro("filialId", e.value)} options={filiais} optionValue="id" optionLabel="nome" placeholder="Todas" showClear filter />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Situação da recorrência</label>
                            <CustomDropdown value={filtros.ativo} onChange={e => setFiltro("ativo", e.value)} options={ATIVO_OPTIONS} placeholder="Todas" showClear />
                        </div>
                        {isAdmin && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground/80">Usuário</label>
                                <CustomDropdown value={filtros.usuarioId} onChange={e => setFiltro("usuarioId", e.value)} options={usuarios} optionValue="id" optionLabel="nome" placeholder="Todos" showClear filter />
                            </div>
                        )}
                        <div className="flex flex-col gap-1.5">
                            <Input label="Fornecedor" value={fornecedorBusca} onChange={e => setFornecedorBusca(e.target.value)} placeholder="Buscar por fornecedor" />
                        </div>

                        <div className="ml-auto flex items-center gap-1 bg-muted rounded-md p-1">
                            <button
                                type="button"
                                onClick={() => setVisualizacao("calendario")}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${visualizacao === "calendario" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
                            >
                                <i className="fa fa-calendar mr-1.5" /> Calendário
                            </button>
                            <button
                                type="button"
                                onClick={() => setVisualizacao("lista")}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${visualizacao === "lista" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
                            >
                                <i className="fa fa-list mr-1.5" /> Lista
                            </button>
                        </div>
                    </div>
                </div>

                {loading && <Loading label="Carregando despesas recorrentes..." />}

                {!loading && visualizacao === "calendario" && (
                    <CalendarioDespesasRecorrentes
                        mes={mes}
                        ocorrencias={ocorrenciasFiltradas}
                        onMesChange={setMes}
                        onSelecionar={ev => setOcorrenciaSelecionadaId(ev.id)}
                    />
                )}

                {!loading && visualizacao === "lista" && (
                    <ListaPendenciasDespesasRecorrentes
                        ocorrencias={ocorrenciasFiltradas}
                        onSelecionar={ev => setOcorrenciaSelecionadaId(ev.id)}
                    />
                )}
            </div>

            <ModalCadastroDespesaRecorrente
                isOpen={modalCadastroAberto}
                onClose={() => { setModalCadastroAberto(false); setDespesaRecorrenteEditando(null); }}
                despesaRecorrente={despesaRecorrenteEditando}
                onSalvo={aoSalvarCadastro}
            />

            <DetalheOcorrenciaDespesaRecorrente
                isOpen={!!ocorrenciaSelecionadaId}
                ocorrenciaId={ocorrenciaSelecionadaId}
                onClose={() => setOcorrenciaSelecionadaId(null)}
                onAtualizado={carregar}
                onEditarRecorrencia={abrirEdicaoRecorrencia}
            />
        </main>
    );
}

function KpiCard({ label, value, icon, className }) {
    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${className}`}>
                <i className={icon} />
            </div>
            <div>
                <div className="text-xl font-bold text-foreground leading-tight">{value}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
            </div>
        </div>
    );
}
